// src/components/Usuarios.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import * as XLSX from 'xlsx';
import { FaDownload, FaSpinner, FaCheckCircle } from 'react-icons/fa';
import Logo from './Logo';

// --- Modal para Desactivar Usuarios en Masa ---
function BulkDisableModal({ isOpen, onClose }) {
  const [step, setStep] = useState('upload'); // 'upload' | 'sheet-selection' | 'column-selection' | 'preview' | 'result'
  const [workbook, setWorkbook] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [headers, setHeaders] = useState([]);
  const [rawData, setRawData] = useState([]);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [extractedIds, setExtractedIds] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [modalError, setModalError] = useState('');

  useEffect(() => {
    if (!isOpen) resetState();
  }, [isOpen]);

  const resetState = () => {
    setStep('upload');
    setWorkbook(null);
    setSheets([]);
    setSelectedSheet('');
    setHeaders([]);
    setRawData([]);
    setSelectedColumn('');
    setExtractedIds([]);
    setDragActive(false);
    setIsLoading(false);
    setResult(null);
    setModalError('');
  };

  const getAuthHeaders = async () => {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    if (!idToken) throw new Error('No se pudo obtener el ID token');
    return {
      'Authorization': `Bearer ${idToken}`,
      'Content-Type': 'application/json'
    };
  };

  const processFile = (file) => {
    setModalError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        setWorkbook(wb);
        setSheets(wb.SheetNames);
        if (wb.SheetNames.length === 1) {
          extractSheetData(wb, wb.SheetNames[0]);
        } else {
          setStep('sheet-selection');
        }
      } catch {
        setModalError('Error al leer el archivo. Verifica que sea un Excel válido.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const extractSheetData = (wb, sheetName) => {
    const ws = wb.Sheets[sheetName];
    const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    if (jsonData.length < 2) {
      setModalError('El archivo no tiene datos suficientes (necesita al menos encabezado y una fila).');
      return;
    }
    const headerRow = jsonData[0].map(h => String(h).trim());
    const dataRows = jsonData.slice(1);
    setSelectedSheet(sheetName);
    setHeaders(headerRow);
    setRawData(dataRows);
    setSelectedColumn('');
    setExtractedIds([]);
    setStep('column-selection');
  };

  const handleColumnClick = (colIndex) => {
    setSelectedColumn(colIndex);
    const ids = rawData
      .map(row => String(row[colIndex] ?? '').trim())
      .filter(id => id !== '');
    setExtractedIds(ids);
    setModalError('');
  };

  const handleProceedToPreview = () => {
    if (selectedColumn === '') {
      setModalError('Debes seleccionar una columna.');
      return;
    }
    if (extractedIds.length === 0) {
      setModalError('La columna seleccionada no tiene datos.');
      return;
    }
    setModalError('');
    setStep('preview');
  };

  const handleConfirm = async () => {
    const confirmed = window.confirm(
      `¿Estás seguro de que deseas desactivar ${extractedIds.length} usuario(s)?\n\n` +
      `Esta acción impedirá que inicien sesión, pero puede revertirse.`
    );
    if (!confirmed) return;

    setIsLoading(true);
    setModalError('');
    try {
      const authHeaders = await getAuthHeaders();
      const response = await axios.delete(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v2/users',
        {
          headers: authHeaders,
          data: { mode: 'massive', user_ids: extractedIds }
        }
      );
      if (response.data.ok) {
        setResult(response.data.data);
        setStep('result');
      } else {
        setModalError(response.data.error || 'Error al procesar la solicitud.');
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setModalError('No tienes permisos para realizar esta acción.');
      } else if (err.response?.status === 400) {
        setModalError(err.response.data?.error || 'Solicitud inválida.');
      } else {
        setModalError('Error al comunicarse con el servidor. Inténtalo de nuevo.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(e.type === 'dragenter' || e.type === 'dragover');
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  if (!isOpen) return null;

  const allSteps = [
    { key: 'upload', label: 'Cargar archivo' },
    { key: 'sheet-selection', label: 'Seleccionar hoja' },
    { key: 'column-selection', label: 'Elegir columna' },
    { key: 'preview', label: 'Confirmar' },
    { key: 'result', label: 'Resultado' },
  ];
  const visibleSteps = sheets.length <= 1
    ? allSteps.filter(s => s.key !== 'sheet-selection')
    : allSteps;
  const currentStepIndex = visibleSteps.findIndex(s => s.key === step);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content bulk-disable-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="modal-header">
          <h3>Desactivar Usuarios en Masa</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Indicador de pasos */}
        <div className="bulk-step-indicator">
          {visibleSteps.map((s, idx) => (
            <React.Fragment key={s.key}>
              <div className={`bulk-step-item ${idx <= currentStepIndex ? 'active' : ''} ${idx === currentStepIndex ? 'current' : ''}`}>
                <div className="bulk-step-dot">{idx + 1}</div>
                <div className="bulk-step-label">{s.label}</div>
              </div>
              {idx < visibleSteps.length - 1 && (
                <div className={`bulk-step-connector ${idx < currentStepIndex ? 'active' : ''}`} />
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Mensaje de error */}
        {modalError && (
          <div className="message message-error" style={{ margin: '0 1.5rem' }}>
            {modalError}
          </div>
        )}

        {/* Cuerpo del modal */}
        <div className="bulk-modal-body">

          {/* Paso 1: Cargar archivo */}
          {step === 'upload' && (
            <div className="bulk-upload-section">
              <p className="bulk-upload-description">
                Sube un archivo Excel con las cédulas de los usuarios a desactivar.
                Luego podrás indicar qué columna las contiene.
              </p>
              <div
                className={`bulk-upload-area ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="bulk-upload-content">
                  <div className="bulk-upload-icon">📊</div>
                  <p className="bulk-upload-text">
                    Arrastra tu archivo aquí o{' '}
                    <span className="bulk-upload-link">haz clic para seleccionar</span>
                  </p>
                  <p className="bulk-upload-hint">Formatos soportados: .xlsx, .xls</p>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="bulk-upload-input"
                    onChange={e => e.target.files?.[0] && processFile(e.target.files[0])}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Paso 2: Selección de hoja */}
          {step === 'sheet-selection' && (
            <div>
              <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
                El archivo tiene varias hojas. Selecciona la que contiene los datos:
              </p>
              <div className="bulk-sheet-list">
                {sheets.map(sheet => (
                  <div
                    key={sheet}
                    className="bulk-sheet-item"
                    onClick={() => extractSheetData(workbook, sheet)}
                  >
                    📄 {sheet}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Paso 3: Selección de columna */}
          {step === 'column-selection' && (
            <div>
              <p style={{ marginBottom: '1rem', color: '#6b7280' }}>
                Hoja: <strong>{selectedSheet}</strong>. Selecciona la columna que contiene las cédulas:
              </p>
              <div className="bulk-column-grid">
                {headers.map((header, index) => (
                  <div
                    key={index}
                    className={`bulk-column-option ${selectedColumn === index ? 'selected' : ''}`}
                    onClick={() => handleColumnClick(index)}
                  >
                    <div className="bulk-column-letter">{String.fromCharCode(65 + index)}</div>
                    <div className="bulk-column-name">{header || '(Sin encabezado)'}</div>
                    {selectedColumn === index && (
                      <div className="bulk-column-preview">
                        {rawData.slice(0, 3).map((row, i) => (
                          <div key={i} className="bulk-column-preview-value">
                            {String(row[index] ?? '').substring(0, 25) || '—'}
                          </div>
                        ))}
                        {rawData.length > 3 && (
                          <div className="bulk-column-preview-more">
                            +{rawData.length - 3} registros más
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {selectedColumn !== '' && (
                <div className="bulk-selection-summary">
                  ✓ Se encontraron <strong>{extractedIds.length}</strong> cédulas en &ldquo;{headers[selectedColumn]}&rdquo;
                </div>
              )}
            </div>
          )}

          {/* Paso 4: Previsualización y confirmación */}
          {step === 'preview' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1.5rem' }}>
                <div className="stat-card warning" style={{ maxWidth: '200px', width: '100%' }}>
                  <div className="stat-number">{extractedIds.length}</div>
                  <div className="stat-label">usuarios a desactivar</div>
                </div>
              </div>
              <p style={{ color: '#6b7280', marginBottom: '1rem' }}>
                Los usuarios con las siguientes cédulas serán desactivados y no podrán iniciar sesión.
                Esta acción es <strong>reversible</strong>.
              </p>
              <div className="bulk-ids-list">
                {extractedIds.map((id, index) => (
                  <div key={index} className="bulk-id-item">
                    <span className="bulk-id-index">{index + 1}</span>
                    <span className="bulk-id-value">{id}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Paso 5: Resultado */}
          {step === 'result' && result && (
            <div>
              <div className="bulk-result-stats">
                <div className="stat-card success">
                  <div className="stat-number">{result.disabled.length}</div>
                  <div className="stat-label">Desactivados</div>
                </div>
                <div className="stat-card warning">
                  <div className="stat-number">{result.not_found.length}</div>
                  <div className="stat-label">No encontrados</div>
                </div>
                <div className="stat-card bulk-error-card">
                  <div className="stat-number bulk-error-number">{result.errors.length}</div>
                  <div className="stat-label">Con errores</div>
                </div>
              </div>

              {result.disabled.length > 0 && (
                <div className="bulk-result-section">
                  <h4 className="bulk-result-section-title bulk-title-success">✓ Desactivados exitosamente</h4>
                  <div className="bulk-result-list">
                    {result.disabled.map((u, i) => (
                      <div key={i} className="bulk-result-item bulk-item-success">
                        <span className="bulk-result-id">{u.user_id}</span>
                        <span className="bulk-result-name">{u.user_name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.not_found.length > 0 && (
                <div className="bulk-result-section">
                  <h4 className="bulk-result-section-title bulk-title-warning">⚠ No encontrados en el sistema</h4>
                  <div className="bulk-result-list">
                    {result.not_found.map((u, i) => (
                      <div key={i} className="bulk-result-item bulk-item-warning">
                        <span className="bulk-result-id">{u.user_id}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {result.errors.length > 0 && (
                <div className="bulk-result-section">
                  <h4 className="bulk-result-section-title bulk-title-error">✗ Con errores</h4>
                  <div className="bulk-result-list">
                    {result.errors.map((u, i) => (
                      <div key={i} className="bulk-result-item bulk-item-error">
                        <span className="bulk-result-id">{u.user_id}</span>
                        <span className="bulk-result-name">{u.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer con acciones */}
        <div className="bulk-modal-footer">
          {(step === 'upload' || step === 'sheet-selection') && (
            <button className="btn btn-outline" onClick={onClose}>Cancelar</button>
          )}
          {step === 'column-selection' && (
            <>
              <button className="btn btn-outline" onClick={() => setStep(sheets.length > 1 ? 'sheet-selection' : 'upload')}>
                ← Atrás
              </button>
              <button
                className="btn btn-primary"
                onClick={handleProceedToPreview}
                disabled={selectedColumn === ''}
              >
                Ver resumen →
              </button>
            </>
          )}
          {step === 'preview' && (
            <>
              <button className="btn btn-outline" onClick={() => setStep('column-selection')} disabled={isLoading}>
                ← Atrás
              </button>
              <button
                className="btn btn-danger"
                onClick={handleConfirm}
                disabled={isLoading}
                style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
              >
                {isLoading && <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />}
                {isLoading ? 'Procesando...' : `Desactivar ${extractedIds.length} usuarios`}
              </button>
            </>
          )}
          {step === 'result' && (
            <button className="btn btn-outline" onClick={onClose}>Cerrar</button>
          )}
        </div>
      </div>
    </div>
  );
}

function Usuarios() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [nextToken, setNextToken] = useState(null);
  const [prevTokens, setPrevTokens] = useState([]);
  const [searchUserId, setSearchUserId] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [retryCount, setRetryCount] = useState(0);
  
  // Estados para el modal de edición de usuario
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const [editableData, setEditableData] = useState({});
  const [originalData, setOriginalData] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [disableLoading, setDisableLoading] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [disableConfirmText, setDisableConfirmText] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+57'); // Estado para el indicativo de país
  
  // Estados para la descarga de usuarios
  const [downloadStatus, setDownloadStatus] = useState('idle'); // 'idle' | 'loading' | 'success'
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });

  // Estado para el modal de desactivación masiva
  const [showBulkModal, setShowBulkModal] = useState(false);
  
  // Estados para los filtros
  const [companies, setCompanies] = useState([]);
  const [stores, setStores] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedStore, setSelectedStore] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);
  
  // Estados para listas desplegables del modal de edición
  const [modalCompanies, setModalCompanies] = useState([]);
  const [modalBrands, setModalBrands] = useState([]);
  const [modalStores, setModalStores] = useState([]);
  const [modalLoadingOptions, setModalLoadingOptions] = useState(false);
  
  const navigate = useNavigate();

  // Función para obtener headers con autenticación Cognito
  const getAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      if (!idToken) {
        throw new Error('No se pudo obtener el ID token');
      }
      
      return {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error al obtener el token de autenticación:', error);
      throw error;
    }
  };

  // Cargar las compañías al montar el componente
  useEffect(() => {
    fetchCompanies();
  }, []);

  // Efecto para cargar tiendas cuando cambia la compañía seleccionada
  useEffect(() => {
    if (selectedCompany) {
      fetchStores(selectedCompany);
    }
  }, [selectedCompany]);

  // Efecto para cargar usuarios cuando cambian los filtros
  useEffect(() => {
    if (selectedCompany) {
      fetchUsers();
    }
  }, [selectedCompany, selectedStore]);

  // Función para obtener las compañías
  const fetchCompanies = async () => {
    setLoadingOptions(true);
    try {
              const headers = await getAuthHeaders();
        const response = await axios.get(
          'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
          {
            headers,
            params: {
              info_type: 'companies',
              tenant_id: localStorage.getItem('tenant_id')
            }
          }
        );

      if (response.status === 200 && response.data.companies) {
        const companiesList = response.data.companies;
        setCompanies(companiesList);
        
        // Seleccionar automáticamente la primera compañía
        if (companiesList.length > 0) {
          setSelectedCompany(companiesList[0]);
        }
      }
    } catch (error) {
      console.error('Error al obtener las compañías:', error);
      setError('Error al cargar las compañías. Por favor, recarga la página.');
    } finally {
      setLoadingOptions(false);
    }
  };

  // Función para obtener las tiendas según la compañía seleccionada
  const fetchStores = async (companyName) => {
    if (!companyName) {
      setStores([]);
      return;
    }

    setLoadingOptions(true);
    try {
              const headers = await getAuthHeaders();
        const response = await axios.get(
          'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
          {
            headers,
            params: {
              info_type: 'create_user_post_company',
              company_name: companyName,
              tenant_id: localStorage.getItem('tenant_id')
            }
          }
        );

      if (response.status === 200 && response.data.stores) {
        const storesList = response.data.stores;
        setStores(storesList);
        
        // Inicialmente no seleccionamos ninguna tienda
        setSelectedStore('');
      }
    } catch (error) {
      console.error('Error al obtener las tiendas:', error);
      setError('Error al cargar las tiendas. Por favor, intenta seleccionar otra compañía.');
    } finally {
      setLoadingOptions(false);
    }
  };

  const fetchUsers = async (startKey = null, userId = null, isNewSearch = false) => {
    setLoading(true);
    setError('');
    
    let params = {
      company_name: selectedCompany || 'REFURBI'
      // Removido el límite de 20 - mostrar todos los registros que envíe el backend
    };

    // Añadir filtro de tienda solo si está seleccionada y no está vacía
    if (selectedStore) {
      params.store_name = selectedStore;
    }

    if (startKey) {
      params.startKey = startKey;
    }

    if (userId) {
      params.user_id = userId;
    }

          try {
        const headers = await getAuthHeaders();
        const response = await axios.get(
          'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v2/users',
          {
            headers,
            params
          }
        );

      if (response.status === 200) {
        // Manejar diferentes estructuras de respuesta
        let usersData = [];
        
        if (userId) {
          // Cuando busca por ID específico, el API devuelve { item: {...} }
          if (response.data.item) {
            usersData = [response.data.item];
          }
        } else {
          // Cuando lista usuarios, el API devuelve { items: [...] }
          usersData = response.data.items || [];
        }
        
        setUsers(usersData);
        
        // Reiniciar navegación ya que no hay paginación
        setPrevTokens([]);
        setCurrentPage(1);
        
        // Como el backend envía todos los registros, no necesitamos paginación
        setNextToken(null); // No hay paginación - todos los registros se muestran de una vez
        
        setRetryCount(0); // Reiniciamos el contador de reintentos
      }
    } catch (error) {
      console.error('Error al obtener usuarios:', error);
      
      // Verificar si es un error 404 (usuario no encontrado)
      if (error.response && error.response.status === 404) {
        // Para errores 404, no reintentamos y mostramos mensaje específico
        if (userId) {
          setError(`Usuario con ID "${userId}" no encontrado.`);
        } else {
          setError('No se encontraron usuarios con los filtros seleccionados.');
        }
        setUsers([]); // Limpiar la lista de usuarios
        setRetryCount(0); // Reiniciar contador
      } else if (retryCount < 3) {
        // Solo reintentamos para otros tipos de errores (no 404)
        setRetryCount(prevCount => prevCount + 1);
        setTimeout(() => {
          fetchUsers(startKey, userId, isNewSearch);
        }, 1000); // Esperamos 1 segundo antes de reintentar
      } else {
        setError('No se pudieron cargar los usuarios después de varios intentos. Por favor, inténtalo de nuevo más tarde.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyChange = (e) => {
    const company = e.target.value;
    setSelectedCompany(company);
    setSelectedStore(''); // Resetear la tienda seleccionada
    setPrevTokens([]); // Resetear la paginación
    setCurrentPage(1);
  };

  const handleStoreChange = (e) => {
    const store = e.target.value;
    setSelectedStore(store);
    setPrevTokens([]); // Resetear la paginación
    setCurrentPage(1);
  };

  // Función deshabilitada - no hay paginación ya que se muestran todos los registros
  const handleNextPage = () => {
    // No hacer nada - todos los registros se muestran en una sola página
  };

  // Función deshabilitada - no hay paginación ya que se muestran todos los registros
  const handlePrevPage = () => {
    // No hacer nada - todos los registros se muestran en una sola página
  };

  const handleSearch = () => {
    if (searchUserId.trim()) {
      fetchUsers(null, searchUserId.trim(), true);
    } else {
      // Si el campo está vacío, volvemos a cargar todos los usuarios
      fetchUsers(null, null, true);
    }
  };

  const handleCreateUser = () => {
    navigate('/crear-usuario');
  };

  // Función para formatear fechas
  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      // Si es un número, lo tratamos como epoch
      if (typeof timestamp === 'number' || !isNaN(Number(timestamp))) {
        const epoch = Number(timestamp);
        // Verificar si es en segundos (10 dígitos) o milisegundos (13 dígitos)
        const milliseconds = epoch < 10000000000 ? epoch * 1000 : epoch;
        
        // Crear la fecha usando UTC para evitar problemas de zona horaria
        const date = new Date(milliseconds);
        
        if (isNaN(date.getTime())) {
          return '-';
        }
        
        // Formatear usando los métodos UTC
        const day = String(date.getUTCDate()).padStart(2, '0');
        const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // +1 porque los meses comienzan en 0
        const year = date.getUTCFullYear();
        
        return `${day}/${month}/${year}`;
      } 
      
      // Si es string, intentamos parsearlo como fecha normal
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) {
        return '-';
      }
      
      // Formatear usando los métodos UTC
      const day = String(date.getUTCDate()).padStart(2, '0');
      const month = String(date.getUTCMonth() + 1).padStart(2, '0');
      const year = date.getUTCFullYear();
      
      return `${day}/${month}/${year}`;
    } catch (e) {
      console.error('Error al formatear fecha:', e);
      return '-';
    }
  };

  // Función para abrir el modal de edición de usuario
  const handleUserClick = (user) => {
    setSelectedUser(user);
    setShowUserModal(true);
    setActiveTab('general');
    
    // Determinar el indicativo de país basado en user_country del localStorage
    const userCountry = localStorage.getItem('user_country') || '';
    let countryCode = '+57'; // Colombia por defecto
    
    if (userCountry === 'CHILE') {
      countryCode = '+56';
    } else if (userCountry === 'MEXICO') {
      countryCode = '+52';
    }
    
    // Separar teléfono y código de país si ya existe
    const userPhone = user.user_phone || '';
    let phoneNumber = userPhone;
    let currentCountryCode = countryCode;
    
    // Si el teléfono ya tiene un indicativo, separarlo
    const phoneMatch = userPhone.match(/^(\+\d{1,4})(.*)$/);
    if (phoneMatch) {
      currentCountryCode = phoneMatch[1];
      phoneNumber = phoneMatch[2];
    }
    
    setPhoneCountryCode(currentCountryCode);
    
    // Inicializar datos editables (sin store_id)
    const initialData = {
      company_name: user.company_name || '',
      user_brand: user.user_brand || '',
      user_name: user.user_name || '',
      store_name: user.store_name || '',
      user_email: user.user_email || '',
      user_phone: phoneNumber // Solo el número sin indicativo
    };
    
    // Cargar datos para las listas desplegables del modal
    fetchModalCompanies();
    if (user.company_name) {
      fetchModalBrandsAndStores(user.company_name);
    }
    
    setEditableData(initialData);
    setOriginalData(initialData);
    setHasChanges(false);
  };

  // Función para cerrar el modal
  const closeUserModal = () => {
    setShowUserModal(false);
    setSelectedUser(null);
    setEditableData({});
    setOriginalData({});
    setHasChanges(false);
    setShowDisableConfirm(false);
    setDisableConfirmText('');
    // Limpiar datos del modal
    setModalCompanies([]);
    setModalBrands([]);
    setModalStores([]);
  };

  // Función para manejar cambios en los campos editables
  const handleFieldChange = (fieldName, value) => {
    const newData = { ...editableData, [fieldName]: value };
    
    // Si cambia la compañía, resetear marca y tienda y cargar nuevas opciones
    if (fieldName === 'company_name') {
      newData.user_brand = '';
      newData.store_name = '';
      fetchModalBrandsAndStores(value);
    }
    
    setEditableData(newData);
    
    // Verificar si hay cambios comparando con los datos originales
    const hasAnyChanges = Object.keys(newData).some(
      key => newData[key] !== originalData[key]
    );
    setHasChanges(hasAnyChanges);
  };

  // Función para actualizar usuario
  const handleUpdateUser = async () => {
    if (!selectedUser || !hasChanges) return;
    
    const confirmed = window.confirm(
      '¿Estás seguro de que deseas actualizar los datos de este usuario?'
    );
    
    if (!confirmed) return;
    
    setUpdateLoading(true);
    try {
      const headers = await getAuthHeaders();
      
      // Combinar indicativo de país con número de teléfono
      const updateData = { ...editableData };
      if (editableData.user_phone && editableData.user_phone.trim()) {
        updateData.user_phone = phoneCountryCode + editableData.user_phone.trim();
      }
      
      // Agregar store_id igual a store_name para mantener compatibilidad
      if (updateData.store_name) {
        updateData.store_id = updateData.store_name;
      }
      
      const response = await axios.patch(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v2/users',
        {
          mode: 'update',
          user_id: selectedUser.user_id,
          ...updateData
        },
        { headers }
      );

      if (response.status === 200) {
        alert('Usuario actualizado exitosamente');
        closeUserModal();
        // Recargar usuarios para reflejar los cambios
        fetchUsers();
      }
    } catch (error) {
      console.error('Error al actualizar usuario:', error);
      alert('Error al actualizar el usuario. Por favor, inténtalo de nuevo.');
    } finally {
      setUpdateLoading(false);
    }
  };

  // Función para obtener todos los usuarios en lotes
  const fetchAllUsersForExport = async () => {
    setDownloadStatus('loading');
    setDownloadProgress({ current: 0, total: 0 });
    
    let allUsers = [];
    let startKey = null;
    let batchNumber = 1;
    let hasMoreData = true;
    
    try {
      const headers = await getAuthHeaders();
      
      console.log(`🚀 Iniciando descarga completa de usuarios en lotes de 50...`);
      
      while (hasMoreData) {
        console.log(`📥 Obteniendo lote ${batchNumber} de usuarios...`);
        
        const params = {
          mode: 'all_users',
          limit: 50,
          tenant_id: localStorage.getItem('tenant_id')
        };
        
        if (startKey) {
          params.startKey = startKey;
          console.log(`🔑 Usando startKey para continuar paginación: ${startKey.substring(0, 50)}...`);
        }
        
        console.log(`📋 Parámetros de la solicitud:`, params);
        
        const response = await axios.get(
          'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v2/users',
          {
            headers,
            params
          }
        );
        
        console.log(`📊 Respuesta del lote ${batchNumber}:`, {
          status: response.status,
          itemsCount: response.data?.items?.length || 0,
          hasNextToken: !!response.data?.nextToken,
          nextTokenPreview: response.data?.nextToken ? response.data.nextToken.substring(0, 50) + '...' : 'null'
        });
        
        if (response.status === 200 && response.data && response.data.items && Array.isArray(response.data.items)) {
          const batchUsers = response.data.items;
          
          if (batchUsers.length > 0) {
            allUsers = [...allUsers, ...batchUsers];
            setDownloadProgress({ current: allUsers.length, total: allUsers.length });
            console.log(`✅ Lote ${batchNumber}: ${batchUsers.length} usuarios obtenidos. Total acumulado: ${allUsers.length}`);
            
            // Verificar si hay más páginas
            if (response.data.nextToken && response.data.nextToken.trim() !== '') {
              startKey = response.data.nextToken;
              batchNumber++;
              console.log(`🔄 Hay más datos disponibles, continuando con el siguiente lote...`);
            } else {
              hasMoreData = false;
              console.log(`🏁 No hay más datos disponibles (nextToken está vacío o nulo)`);
            }
          } else {
            console.log(`⚠️ Lote ${batchNumber} está vacío, deteniendo la descarga`);
            hasMoreData = false;
          }
        } else {
          console.error(`❌ Respuesta inválida en lote ${batchNumber}:`, {
            status: response.status,
            hasData: !!response.data,
            hasItems: !!(response.data && response.data.items),
            isArray: response.data?.items ? Array.isArray(response.data.items) : false
          });
          hasMoreData = false;
        }
        
        // Pausa pequeña entre lotes para evitar rate limiting
        if (hasMoreData) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
      
      console.log(`🎯 ¡DESCARGA COMPLETA! Total de usuarios obtenidos: ${allUsers.length} en ${batchNumber} lotes`);
      return allUsers;
      
    } catch (error) {
      console.error('❌ Error al obtener todos los usuarios:', error);
      console.error('❌ Detalles del error:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      setDownloadStatus('idle');
      throw error;
    }
  };

  // Función para exportar usuarios a Excel
  const handleExportAllUsers = async () => {
    try {
      setDownloadStatus('loading');
      setError('');
      
      console.log("📥 Iniciando descarga de todos los usuarios...");
      
      // Obtener todos los usuarios
      const allUsers = await fetchAllUsersForExport();
      
      if (!allUsers || allUsers.length === 0) {
        setError('No hay usuarios para exportar.');
        setDownloadStatus('idle');
        return;
      }
      
      // Definir las columnas que queremos en el Excel
      const columnMapping = {
        'user_id': 'ID de Usuario',
        'user_name': 'Nombre Completo',
        'user_email': 'Correo Electrónico',
        'user_phone': 'Teléfono',
        'user_type': 'Tipo de Usuario',
        'user_level': 'Nivel de Usuario',
        'company_name': 'Nombre de la Compañía',
        'store_name': 'Tienda asignada',
        'store_id': 'ID de la Tienda',
        'store_city': 'Ciudad de la Tienda',
        'user_brand': 'Marca Asignada',
        'creation_date': 'Fecha de Creación',
        'last_modified_date': 'Última Modificación',
        'tenant_id': 'ID del Tenant'
      };
      
      // Preparar los datos para Excel
      const excelData = allUsers.map(user => {
        const row = {};
        Object.keys(columnMapping).forEach(field => {
          const columnName = columnMapping[field];
          let value = user[field];
          
          // Formatear fechas
          if (field === 'creation_date' || field === 'last_modified_date') {
            if (value != null && !isNaN(Number(value))) {
              const epoch = Number(value);
              const milliseconds = epoch < 10000000000 ? epoch * 1000 : epoch;
              const dateObj = new Date(milliseconds);
              if (!isNaN(dateObj.getTime())) {
                row[columnName] = dateObj;
              } else {
                row[columnName] = '';
              }
            } else {
              row[columnName] = '';
            }
          } else {
            row[columnName] = value !== undefined ? String(value) : '';
          }
        });
        return row;
      });
      
      // Crear el libro de trabajo
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData, {
        dateNF: 'dd/mm/yyyy hh:mm:ss'
      });
      
      // Ajustar ancho de columnas
      const colWidths = [];
      Object.values(columnMapping).forEach((columnName, index) => {
        const maxLength = Math.max(
          columnName.length,
          ...excelData.slice(0, 100).map(row => 
            String(row[columnName] || '').length
          )
        );
        colWidths[index] = { width: Math.min(Math.max(maxLength + 2, 10), 50) };
      });
      worksheet['!cols'] = colWidths;
      
      // Agregar la hoja al libro
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Todos los Usuarios');
      
      // Generar el nombre del archivo con fecha actual
      const now = new Date();
      const dateStr = now.toISOString().slice(0, 10);
      const timeStr = now.toTimeString().slice(0, 5).replace(':', '');
      const fileName = `todos_los_usuarios_${dateStr}_${timeStr}.xlsx`;
      
      // Descargar el archivo
      XLSX.writeFile(workbook, fileName, {
        cellDates: true,
        dateNF: 'dd/mm/yyyy hh:mm:ss'
      });
      
      setDownloadStatus('success');
      console.log(`✅ Excel generado exitosamente: ${fileName} (${allUsers.length} usuarios)`);
      
    } catch (error) {
      console.error('❌ Error al generar el Excel de usuarios:', error);
      setError(`Error al generar el archivo Excel: ${error.message || 'Inténtalo de nuevo.'}`);
      setDownloadStatus('idle');
    }
    
    // Resetear estado 'success' después de un tiempo
    setTimeout(() => {
      if (downloadStatus === 'success') {
        setDownloadStatus('idle');
      }
    }, 3000);
  };

  // Función para reiniciar contraseña
  const handleResetPassword = async () => {
    if (!selectedUser) return;
    
    const confirmed = window.confirm(
      '⚠️ ATENCIÓN: Esta acción no tiene vuelta atrás.\n\n' +
      'Si reinicias este usuario, el mismo deberá entrar a la plataforma ' +
      'para cambiar su contraseña.\n\n' +
      '¿Estás seguro de que deseas continuar?'
    );
    
    if (!confirmed) return;
    
    setResetLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.patch(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v2/users',
        {
          mode: 'reset',
          user_id: selectedUser.user_id
        },
        { headers }
      );

      if (response.status === 200) {
        alert('Contraseña reiniciada exitosamente. El usuario deberá cambiar su contraseña al iniciar sesión.');
        closeUserModal();
      }
    } catch (error) {
      console.error('Error al reiniciar contraseña:', error);
      alert('Error al reiniciar la contraseña. Por favor, inténtalo de nuevo.');
    } finally {
      setResetLoading(false);
    }
  };

  // Función para deshabilitar usuario individualmente
  const handleDisableUser = async () => {
    if (!selectedUser) return;

    setDisableLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.delete(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v2/users',
        {
          headers,
          data: {
            mode: 'individual',
            user_id: selectedUser.user_id
          }
        }
      );
      if (response.data.ok) {
        alert('Usuario deshabilitado exitosamente.');
        closeUserModal();
        fetchUsers();
      } else {
        alert(response.data.error || 'Error al deshabilitar el usuario.');
      }
    } catch (err) {
      if (err.response?.status === 403) {
        alert('No tienes permisos para realizar esta acción.');
      } else if (err.response?.status === 400) {
        alert(err.response.data?.error || 'Solicitud inválida.');
      } else {
        alert('Error al comunicarse con el servidor. Inténtalo de nuevo.');
      }
    } finally {
      setDisableLoading(false);
      setShowDisableConfirm(false);
      setDisableConfirmText('');
    }
  };

  // Configuración de pestañas
  const getTabConfig = () => {
    return [
      {
        id: 'general',
        label: 'Información General',
        icon: '👤',
        fields: ['user_id', 'user_name', 'user_email', 'user_phone', 'user_type']
      },
      {
        id: 'empresa',
        label: 'Empresa y Tienda',
        icon: '🏢',
        fields: ['company_name', 'store_name', 'store_city', 'user_brand']
      },
      {
        id: 'fechas',
        label: 'Fechas',
        icon: '📅',
        fields: ['creation_date', 'last_modified_date']
      }
    ];
  };

  // Función para obtener campos con datos de una pestaña
  const getTabFieldsWithData = (tabId, user) => {
    const tabConfig = getTabConfig().find(tab => tab.id === tabId);
    if (!tabConfig || !user) return [];
    
    return tabConfig.fields.filter(fieldKey => {
      const value = user[fieldKey];
      return value !== undefined && value !== null && value !== '';
    });
  };

  // Función para mapear nombres de campos al español
  const getFieldLabel = (fieldName) => {
    const fieldLabels = {
      user_id: 'ID de Usuario',
      user_name: 'Nombre Completo',
      user_email: 'Correo Electrónico',
      user_phone: 'Teléfono',
      user_type: 'Tipo de Usuario',
      company_name: 'Nombre de la Compañía',
      store_name: 'Tienda Asignada',
      store_city: 'Ciudad de la Tienda',
      user_brand: 'Marca Asignada',
      creation_date: 'Fecha de Creación',
      last_modified_date: 'Última Modificación',
      tenant_id: 'ID del Tenant'
    };
    return fieldLabels[fieldName] || fieldName;
  };

  // Función para verificar si un campo es editable
  const isFieldEditable = (fieldName) => {
    const editableFields = ['company_name', 'user_brand', 'user_name', 'store_name', 'user_email', 'user_phone'];
    return editableFields.includes(fieldName);
  };
  
  // Función para obtener las compañías para el modal
  const fetchModalCompanies = async () => {
    setModalLoadingOptions(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
        {
          headers,
          params: {
            info_type: 'companies',
            tenant_id: localStorage.getItem('tenant_id')
          }
        }
      );

      if (response.status === 200 && response.data.companies) {
        setModalCompanies(response.data.companies);
      }
    } catch (error) {
      console.error('Error al obtener las compañías para el modal:', error);
    } finally {
      setModalLoadingOptions(false);
    }
  };
  
  // Función para obtener marcas y tiendas para el modal
  const fetchModalBrandsAndStores = async (companyName) => {
    if (!companyName) {
      setModalBrands([]);
      setModalStores([]);
      return;
    }

    setModalLoadingOptions(true);
    try {
      const headers = await getAuthHeaders();
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/info',
        {
          headers,
          params: {
            info_type: 'create_user_post_company',
            company_name: companyName,
            tenant_id: localStorage.getItem('tenant_id')
          }
        }
      );

      if (response.status === 200) {
        if (response.data.brands) {
          setModalBrands(response.data.brands);
        }
        if (response.data.stores) {
          setModalStores(response.data.stores);
        }
      }
    } catch (error) {
      console.error('Error al obtener marcas y tiendas para el modal:', error);
    } finally {
      setModalLoadingOptions(false);
    }
  };
  
  // Lista de indicativos de países
  const countryCodeOptions = [
    { code: '+57', country: 'Colombia' },
    { code: '+56', country: 'Chile' },
    { code: '+52', country: 'México' },
    { code: '+54', country: 'Argentina' },
    { code: '+51', country: 'Perú' },
    { code: '+593', country: 'Ecuador' },
    { code: '+58', country: 'Venezuela' },
    { code: '+507', country: 'Panamá' },
    { code: '+506', country: 'Costa Rica' },
    { code: '+1', country: 'Estados Unidos/Canadá' },
    { code: '+34', country: 'España' }
  ];

  return (
    <div className="usuarios-container">
      <header className="header">
        <Logo 
          variant="horizontal" 
          size="medium" 
          onClick={() => navigate('/dashboard')} 
        />
        <div className="header-title">Gestión de Usuarios</div>
        <div className="user-menu">
          <button className="btn btn-outline" onClick={() => navigate('/dashboard')}>Volver al Dashboard</button>
        </div>
      </header>
      
      <div className="container">
        <div className="card animate-fade-in">
          <h2 className="card-title">Gestión de Usuarios</h2>
          
          {/* Búsqueda por identificación personal */}
          <div className="search-section">
            <div className="form-group">
              <label htmlFor="searchUserId" className="form-label">Buscar por identificación personal</label>
              <div className="search-input-container">
                <input 
                  id="searchUserId"
                  type="text" 
                  className="form-input"
                  placeholder="Ingresa el ID del usuario" 
                  value={searchUserId} 
                  onChange={(e) => setSearchUserId(e.target.value)}
                />
                <button 
                  className="btn btn-primary" 
                  onClick={handleSearch}
                >
                  Buscar
                </button>
              </div>
            </div>
          </div>
          
          {/* Filtros de compañía y tienda */}
          <div className="filters-section">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="companyFilter" className="form-label">Compañía</label>
                <select
                  id="companyFilter"
                  className="form-select"
                  value={selectedCompany}
                  onChange={handleCompanyChange}
                  disabled={loadingOptions || companies.length === 0}
                >
                  {companies.length === 0 ? (
                    <option value="">Cargando compañías...</option>
                  ) : (
                    companies.map((company, index) => (
                      <option key={index} value={company}>{company}</option>
                    ))
                  )}
                </select>
              </div>
              
              <div className="form-group">
                <label htmlFor="storeFilter" className="form-label">Tienda (Opcional)</label>
                <div className="select-with-clear">
                  <select
                    id="storeFilter"
                    className="form-select"
                    value={selectedStore}
                    onChange={handleStoreChange}
                    disabled={loadingOptions || stores.length === 0 || !selectedCompany}
                  >
                    <option value="">Todas las tiendas</option>
                    {loadingOptions ? (
                      <option value="" disabled>Cargando tiendas...</option>
                    ) : stores.length === 0 ? (
                      <option value="" disabled>No hay tiendas disponibles</option>
                    ) : (
                      stores.map((store, index) => (
                        <option key={index} value={store}>{store}</option>
                      ))
                    )}
                  </select>
                  {selectedStore && (
                    <button 
                      className="clear-select-btn" 
                      onClick={() => setSelectedStore('')}
                      title="Limpiar selección"
                    >
                      ×
                    </button>
                  )}
                </div>
                <div className="input-help-text">Selecciona una tienda específica o deja en blanco para ver todas</div>
              </div>
            </div>
          </div>
          
          <div className="action-buttons">
            <button
              className="btn btn-secondary"
              onClick={handleCreateUser}
            >
              Crear Usuario
            </button>
            <button
              className="btn btn-danger"
              onClick={() => setShowBulkModal(true)}
              title="Desactivar múltiples usuarios cargando un archivo Excel con sus cédulas"
            >
              Desactivar masivo
            </button>
            <button
              className="btn btn-primary download-all-btn"
              onClick={handleExportAllUsers}
              disabled={downloadStatus === 'loading'}
              title="Descargar todos los usuarios del sistema en formato Excel"
            >
              {downloadStatus === 'loading' && <FaSpinner className="spin" />}
              {downloadStatus === 'success' && <FaCheckCircle />}
              {downloadStatus === 'idle' && <FaDownload />}
              <span style={{ marginLeft: '8px' }}>
                {downloadStatus === 'loading' 
                   ? `Descargando usuarios... ${downloadProgress.current > 0 ? `(${downloadProgress.current})` : ''}` 
                   : downloadStatus === 'success'
                     ? 'Descarga completada'
                     : 'Descargar todos los usuarios'}
              </span>
            </button>
          </div>
          
          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Cargando usuarios...</p>
            </div>
          ) : error ? (
            <div className="message message-error">{error}</div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>ID de tienda</th>
                      <th>ID de usuario</th>
                      <th>Fecha de creación</th>
                      <th>Última modificación</th>
                      <th>Ciudad</th>
                      <th>Nombre de tienda</th>
                      <th>Marca</th>
                      <th>Email</th>
                      <th>Nombre</th>
                      <th>Teléfono</th>
                      <th>Tipo de usuario</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.length > 0 ? (
                      users.map((user, index) => (
                        <tr 
                          key={index}
                          className="user-row clickable-row"
                          onClick={() => handleUserClick(user)}
                          title="Haz clic para editar este usuario"
                        >
                          <td>{user.store_id || '-'}</td>
                          <td>{user.user_id || '-'}</td>
                          <td>{formatDate(user.creation_date) || '-'}</td>
                          <td>{formatDate(user.last_modified_date) || '-'}</td>
                          <td>{user.store_city || '-'}</td>
                          <td>{user.store_name || '-'}</td>
                          <td>{user.user_brand || '-'}</td>
                          <td>{user.user_email || '-'}</td>
                          <td>{user.user_name || '-'}</td>
                          <td>{user.user_phone || '-'}</td>
                          <td>{user.user_level || '-'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="11" className="no-results">No se encontraron usuarios</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Paginación oculta - el backend envía todos los registros de una vez */}
              {false && (
                <div className="pagination">
                  <button 
                    className="btn btn-outline pagination-btn"
                    onClick={handlePrevPage}
                    disabled={prevTokens.length === 0}
                  >
                    Anterior
                  </button>
                  <span className="pagination-info">Página {currentPage}</span>
                  <button 
                    className="btn btn-outline pagination-btn"
                    onClick={handleNextPage}
                    disabled={!nextToken}
                  >
                    Siguiente
                  </button>
                </div>
              )}
            </>
          )}
          
          <div className="form-actions">
            <button 
              className="btn btn-outline back-button" 
              onClick={() => navigate('/dashboard')}
            >
              Regresar al Menú Principal
            </button>
          </div>
        </div>
      </div>

      {/* Modal de Desactivación Masiva */}
      <BulkDisableModal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
      />

      {/* Modal de Detalles y Edición de Usuario */}
      {showUserModal && selectedUser && (
        <div className="user-modal-overlay" onClick={closeUserModal}>
          <div className="user-modal-container" onClick={(e) => e.stopPropagation()}>
            {/* Header del Modal */}
            <div className="user-modal-header">
              <div className="user-modal-title">
                <h2>Editar Usuario</h2>
                <span className="user-id-badge">
                  ID: {selectedUser.user_id}
                </span>
              </div>
              <div className="user-modal-actions">
                <button 
                  className="update-btn"
                  onClick={handleUpdateUser}
                  disabled={!hasChanges || updateLoading}
                  title={hasChanges ? "Actualizar datos del usuario" : "No hay cambios para guardar"}
                >
                  {updateLoading ? '⏳' : '💾'}
                  <span>Actualizar Datos</span>
                </button>
                <button
                  className="reset-btn"
                  onClick={handleResetPassword}
                  disabled={resetLoading}
                  title="Reiniciar contraseña del usuario"
                >
                  {resetLoading ? '⏳' : '🔄'}
                  <span>Reiniciar Usuario</span>
                </button>
                <button
                  className="disable-btn"
                  onClick={() => { setShowDisableConfirm(true); setDisableConfirmText(''); }}
                  disabled={disableLoading || showDisableConfirm}
                  title="Deshabilitar este usuario"
                >
                  🚫
                  <span>Deshabilitar Usuario</span>
                </button>
                <button
                  className="user-modal-close"
                  onClick={closeUserModal}
                  title="Cerrar"
                >
                  ✕
                </button>
              </div>
            </div>

            {/* Panel de confirmación para deshabilitar usuario */}
            {showDisableConfirm && (
              <div className="disable-confirm-panel">
                <div className="disable-confirm-warning">
                  ⚠️ <strong>Atención:</strong> Estás a punto de deshabilitar al usuario <strong>{selectedUser.user_name || selectedUser.user_id}</strong>.
                  El usuario no podrá iniciar sesión hasta que sea reactivado.
                </div>
                <div className="disable-confirm-input-row">
                  <label className="disable-confirm-label">
                    Para confirmar, escribe <strong>deshabilitar usuario</strong>:
                  </label>
                  <input
                    type="text"
                    className="disable-confirm-input"
                    value={disableConfirmText}
                    onChange={(e) => setDisableConfirmText(e.target.value)}
                    placeholder="deshabilitar usuario"
                    autoFocus
                  />
                </div>
                <div className="disable-confirm-actions">
                  <button
                    className="btn btn-outline"
                    onClick={() => { setShowDisableConfirm(false); setDisableConfirmText(''); }}
                    disabled={disableLoading}
                  >
                    Cancelar
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={handleDisableUser}
                    disabled={disableConfirmText !== 'deshabilitar usuario' || disableLoading}
                  >
                    {disableLoading ? '⏳ Deshabilitando...' : '🚫 Confirmar deshabilitación'}
                  </button>
                </div>
              </div>
            )}

            {/* Contenido Principal */}
            <div className="user-modal-content">
              {/* Navegación de Pestañas */}
              <div className="tabs-navigation">
                {getTabConfig().map(tab => {
                  const fieldsWithData = getTabFieldsWithData(tab.id, selectedUser);
                  const hasData = fieldsWithData.length > 0;
                  
                  return (
                    <button
                      key={tab.id}
                      className={`tab-btn ${activeTab === tab.id ? 'active' : ''} ${!hasData ? 'empty' : ''}`}
                      onClick={() => setActiveTab(tab.id)}
                      disabled={!hasData}
                      title={hasData ? `${tab.label} (${fieldsWithData.length} campos)` : `${tab.label} (sin datos)`}
                    >
                      <span className="tab-icon">{tab.icon}</span>
                      <span className="tab-label">{tab.label}</span>
                      {hasData && <span className="data-count">{fieldsWithData.length}</span>}
                    </button>
                  );
                })}
              </div>

              {/* Contenido de la Pestaña Activa */}
              <div className="tab-content">
                {(() => {
                  const activeTabConfig = getTabConfig().find(tab => tab.id === activeTab);
                  const fieldsWithData = getTabFieldsWithData(activeTab, selectedUser);
                  
                  if (!activeTabConfig || fieldsWithData.length === 0) {
                    return (
                      <div className="no-data">
                        <p>No hay datos disponibles en esta sección</p>
                      </div>
                    );
                  }

                  return (
                    <div className="fields-container">
                      <h3 className="tab-title">
                        {activeTabConfig.icon} {activeTabConfig.label}
                      </h3>
                      <div className="fields-grid">
                        {fieldsWithData.map(fieldKey => {
                          const fieldLabel = getFieldLabel(fieldKey);
                          const isEditable = isFieldEditable(fieldKey);
                          let fieldValue = selectedUser[fieldKey];

                          // Formatear valor según el tipo de campo
                          if (fieldKey === 'creation_date' || fieldKey === 'last_modified_date') {
                            fieldValue = formatDate(fieldValue);
                          } else if (typeof fieldValue === 'object' && fieldValue !== null) {
                            fieldValue = JSON.stringify(fieldValue);
                          } else {
                            fieldValue = String(fieldValue || '');
                          }

                          return (
                            <div key={fieldKey} className="field-item">
                              <label className="field-label">
                                {fieldLabel}
                                {isEditable && <span className="editable-indicator">✏️</span>}
                              </label>
                              {isEditable ? (
                                fieldKey === 'user_phone' ? (
                                  <div className="phone-input-container">
                                    <select
                                      className="country-code-select"
                                      value={phoneCountryCode}
                                      onChange={(e) => setPhoneCountryCode(e.target.value)}
                                    >
                                      {countryCodeOptions.map(option => (
                                        <option key={option.code} value={option.code}>
                                          {option.code} ({option.country})
                                        </option>
                                      ))}
                                    </select>
                                    <input
                                      type="tel"
                                      className="field-input phone-number-input"
                                      value={editableData[fieldKey] || ''}
                                      onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                                      placeholder="Número de teléfono"
                                    />
                                  </div>
                                ) : fieldKey === 'user_email' ? (
                                  <input
                                    type="email"
                                    className="field-input"
                                    value={editableData[fieldKey] || ''}
                                    onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                                    placeholder={`Ingresa ${fieldLabel.toLowerCase()}`}
                                  />
                                ) : fieldKey === 'company_name' ? (
                                  <select
                                    className="field-input"
                                    value={editableData[fieldKey] || ''}
                                    onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                                    disabled={modalLoadingOptions}
                                  >
                                    <option value="">Seleccionar empresa...</option>
                                    {modalCompanies.map((company, index) => (
                                      <option key={index} value={company}>
                                        {company}
                                      </option>
                                    ))}
                                  </select>
                                ) : fieldKey === 'user_brand' ? (
                                  <select
                                    className="field-input"
                                    value={editableData[fieldKey] || ''}
                                    onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                                    disabled={modalLoadingOptions || !editableData.company_name}
                                  >
                                    <option value="">Seleccionar marca...</option>
                                    <option value="ALL_BRANDS">Todas las marcas</option>
                                    {modalBrands.map((brand, index) => (
                                      <option key={index} value={brand}>
                                        {brand}
                                      </option>
                                    ))}
                                  </select>
                                ) : fieldKey === 'store_name' ? (
                                  <select
                                    className="field-input"
                                    value={editableData[fieldKey] || ''}
                                    onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                                    disabled={modalLoadingOptions || !editableData.company_name}
                                  >
                                    <option value="">Seleccionar tienda...</option>
                                    {modalStores.map((store, index) => (
                                      <option key={index} value={store}>
                                        {store}
                                      </option>
                                    ))}
                                  </select>
                                ) : (
                                  <input
                                    type="text"
                                    className="field-input"
                                    value={editableData[fieldKey] || ''}
                                    onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
                                    placeholder={`Ingresa ${fieldLabel.toLowerCase()}`}
                                  />
                                )
                              ) : (
                                <div className="field-value">{fieldValue}</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .action-buttons {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          margin-bottom: 1.5rem;
        }
        
        .download-all-btn {
          display: flex;
          align-items: center;
          position: relative;
          min-width: 220px;
          justify-content: center;
        }
        
        .download-all-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }
        
        .spin {
          animation: spin 1s linear infinite;
        }
        
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @media (max-width: 768px) {
          .action-buttons {
            flex-direction: column;
          }
          
          .download-all-btn {
            min-width: auto;
            width: 100%;
          }
        }
        
        .clickable-row {
          cursor: pointer;
          transition: background-color 0.2s ease;
        }
        
        .clickable-row:hover {
          background-color: #f8f9fa;
        }
        
        .user-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: rgba(0, 0, 0, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 1000;
          padding: 20px;
        }
        
        .user-modal-container {
          background: white;
          border-radius: 12px;
          max-width: 900px;
          width: 100%;
          max-height: 90vh;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
          display: flex;
          flex-direction: column;
        }
        
        .user-modal-header {
          padding: 1.5rem;
          border-bottom: 1px solid #e5e7eb;
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
        }
        
        .user-modal-title h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
        }
        
        .user-id-badge {
          background: rgba(255, 255, 255, 0.2);
          padding: 0.25rem 0.75rem;
          border-radius: 20px;
          font-size: 0.875rem;
          margin-top: 0.5rem;
          display: inline-block;
        }
        
        .user-modal-actions {
          display: flex;
          gap: 0.75rem;
          align-items: center;
        }
        
        .update-btn, .reset-btn, .disable-btn {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.5rem 1rem;
          border: none;
          border-radius: 6px;
          font-size: 0.875rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        
        .update-btn {
          background: #10b981;
          color: white;
        }
        
        .update-btn:disabled {
          background: #6b7280;
          cursor: not-allowed;
        }
        
        .update-btn:not(:disabled):hover {
          background: #059669;
        }
        
        .reset-btn {
          background: #ef4444;
          color: white;
        }

        .disable-btn {
          background: #b45309;
          color: white;
        }

        .disable-btn:disabled {
          background: #6b7280;
          cursor: not-allowed;
        }

        .disable-btn:not(:disabled):hover {
          background: #92400e;
        }

        .disable-confirm-panel {
          background: #fff7ed;
          border-top: 3px solid #f59e0b;
          padding: 1.25rem 1.5rem;
          display: flex;
          flex-direction: column;
          gap: 0.875rem;
        }

        .disable-confirm-warning {
          color: #92400e;
          font-size: 0.9rem;
        }

        .disable-confirm-input-row {
          display: flex;
          flex-direction: column;
          gap: 0.4rem;
        }

        .disable-confirm-label {
          font-size: 0.875rem;
          color: #374151;
        }

        .disable-confirm-input {
          padding: 0.5rem 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 0.875rem;
          max-width: 320px;
          outline: none;
          transition: border-color 0.2s;
        }

        .disable-confirm-input:focus {
          border-color: #f59e0b;
          box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
        }

        .disable-confirm-actions {
          display: flex;
          gap: 0.75rem;
          align-items: center;
        }

        .reset-btn:disabled {
          background: #6b7280;
          cursor: not-allowed;
        }
        
        .reset-btn:not(:disabled):hover {
          background: #dc2626;
        }
        
        .user-modal-close {
          background: rgba(255, 255, 255, 0.2);
          border: none;
          color: white;
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          font-size: 1.25rem;
          transition: background-color 0.2s ease;
        }
        
        .user-modal-close:hover {
          background: rgba(255, 255, 255, 0.3);
        }
        
        .user-modal-content {
          flex: 1;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        
        .tabs-navigation {
          display: flex;
          border-bottom: 1px solid #e5e7eb;
          background: #f9fafb;
        }
        
        .tab-btn {
          padding: 1rem 1.5rem;
          border: none;
          background: transparent;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          font-weight: 500;
          color: #6b7280;
          transition: all 0.2s ease;
          border-bottom: 3px solid transparent;
        }
        
        .tab-btn:hover:not(:disabled) {
          background: #f3f4f6;
          color: #374151;
        }
        
        .tab-btn.active {
          background: white;
          color: #6366f1;
          border-bottom-color: #6366f1;
        }
        
        .tab-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .tab-icon {
          font-size: 1rem;
        }
        
        .data-count {
          background: #6366f1;
          color: white;
          font-size: 0.75rem;
          padding: 0.125rem 0.375rem;
          border-radius: 10px;
          min-width: 1.25rem;
          height: 1.25rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .tab-content {
          flex: 1;
          overflow-y: auto;
          padding: 1.5rem;
        }
        
        .fields-container {
          max-width: 100%;
        }
        
        .tab-title {
          margin: 0 0 1.5rem 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: #374151;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .fields-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
        }
        
        .field-item {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }
        
        .field-label {
          font-weight: 600;
          color: #374151;
          font-size: 0.875rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        
        .editable-indicator {
          color: #10b981;
          font-size: 0.75rem;
        }
        
        .field-value {
          padding: 0.75rem;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 6px;
          color: #374151;
          min-height: 1.5rem;
          word-break: break-word;
        }
        
        .field-input {
          padding: 0.75rem;
          border: 2px solid #e5e7eb;
          border-radius: 6px;
          color: #374151;
          font-size: 0.875rem;
          transition: border-color 0.2s ease;
        }
        
        .field-input:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }
        
        .no-data {
          text-align: center;
          padding: 3rem;
          color: #6b7280;
        }
        
        .phone-input-container {
          display: flex;
          gap: 0.5rem;
          align-items: stretch;
        }
        
        .country-code-select {
          flex: 0 0 auto;
          min-width: 120px;
          padding: 0.75rem 0.5rem;
          border: 2px solid #e5e7eb;
          border-radius: 6px;
          background: white;
          color: #374151;
          font-size: 0.875rem;
          transition: border-color 0.2s ease;
        }
        
        .country-code-select:focus {
          outline: none;
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
        }
        
        .phone-number-input {
          flex: 1;
          margin: 0 !important;
        }
        
        @media (max-width: 768px) {
          .user-modal-container {
            margin: 10px;
            max-height: calc(100vh - 20px);
          }
          
          .tabs-navigation {
            flex-wrap: wrap;
          }
          
          .tab-btn {
            padding: 0.75rem 1rem;
            font-size: 0.8rem;
          }
          
          .fields-grid {
            grid-template-columns: 1fr;
          }
          
          .user-modal-actions {
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .phone-input-container {
            flex-direction: column;
            gap: 0.5rem;
          }
          
          .country-code-select {
            min-width: auto;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
}

export default Usuarios; 